import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { createApi, type Api, type EngineClient } from './api';
import { createClient } from '../engine/api/client';
import type { Principal } from '../engine/api/context';
import { getSupabase } from './supabase';
import { isAuthConfigured } from './runtimeConfig';
import { federatedSession, type FederatedIdentity } from './federatedSession';

// Auth context backed by Supabase Auth (GoTrue) OR a federated (Halo) sign-in. A session is a
// Supabase session, or — for an organisation that signs in with its Halo — a session-shaped view
// of our own short-lived token (lib/federatedSession.ts; the refresh token is an HttpOnly cookie
// the SPA never sees). Either way its access token is sent to our API as a Bearer. supabase-js persists + auto-refreshes it,
// so the api client always reads a fresh token. The token only proves identity — the API
// resolves the tenant from the user's record (no tenant claim in the token) and RLS enforces it.
interface AuthState {
  session: Session | null;
  loading: boolean;
  configured: boolean;
  api: Api;
  /** The engine client (api.client) — what EngineProvider receives. */
  client: EngineClient;
  /** Engine client for the platform-admin surface (prefix /v1/admin). Same token/refresh path. */
  adminClient: EngineClient;
  /** Identity for the engine: who-am-I from GET /v1/me (name, permissions, platform role). */
  principal: Principal;
  /** The resolved principal (GET /v1/me), null until loaded or when signed out. */
  me: Me | null;
  signOut: () => Promise<void>;
}

/** GET /v1/me — the SPA's one source for identity, tenant, role, permissions and platform role. */
export interface Me {
  user: { id: string; email: string | null; name: string | null };
  tenant: { id: string; name: string | null; status: string | null } | null;
  membership: { role: string | null; role_name: string | null; permissions: string[] } | null;
  platform_role: string | null;
  principal: 'user' | 'api_key';
  /** Second factor: whether the organisation requires one for everyone, and this session's assurance. */
  security?: { mfa_required: boolean; aal: 'aal1' | 'aal2' };
}

const AuthContext = createContext<AuthState | null>(null);

/** Decode a JWT payload (no verification — display-only; the API verifies + enforces). */
function decodeJwtPayload(token?: string): Record<string, unknown> {
  const part = token?.split('.')[1];
  if (!part) return {};
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** A Supabase-Session-shaped view of a federated identity, so every consumer keeps one shape. */
function sessionOf(f: FederatedIdentity): Session {
  return {
    access_token: f.accessToken,
    token_type: 'bearer',
    expires_at: Math.floor(f.expiresAt / 1000),
    user: { id: f.userId, email: f.email ?? undefined, user_metadata: {}, app_metadata: { provider: 'halo' } },
  } as unknown as Session;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [federated, setFederated] = useState<FederatedIdentity | null>(federatedSession.get());
  // Start "loading" so guards don't bounce to /login before the session is restored (or
  // an OAuth/magic-link redirect is exchanged). If auth isn't configured, we're not loading.
  const [loading, setLoading] = useState(isAuthConfigured);
  const [fedLoading, setFedLoading] = useState(() => federatedSession.wasActive() && !federatedSession.get());

  // Federated (Halo) sign-in: follow the in-memory session, and after a reload restore it once
  // from the HttpOnly refresh cookie (only when this browser signed in that way before).
  useEffect(() => {
    const off = federatedSession.subscribe(setFederated);
    if (federatedSession.wasActive() && !federatedSession.get()) {
      void federatedSession.restore().finally(() => setFedLoading(false));
    }
    return () => {
      off();
    };
  }, []);

  useEffect(() => {
    if (!isAuthConfigured) return;
    const supabase = getSupabase();
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
      })
      .catch(() => {
        // Never strand the app on the loading spinner if the session read fails.
        if (active) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // The clients are built once: they pull the current access token on each request (fresh
  // after a refresh), so nothing about them changes with the session.
  const clients = useMemo(() => {
    const getToken = async () => {
      if (federatedSession.get() || federatedSession.wasActive()) return federatedSession.token();
      if (!isAuthConfigured) return null;
      const { data } = await getSupabase().auth.getSession();
      return data.session?.access_token ?? null;
    };
    const onUnauthorized = () => {
      // Auth failure from the API → clear the (now-useless) session so guards route to login.
      if (federatedSession.get()) federatedSession.forget();
      if (isAuthConfigured) void getSupabase().auth.signOut();
      setSession(null);
    };
    const api = createApi(getToken, onUnauthorized);
    // Same token + refresh path, pointed at the platform-admin route group.
    const adminClient = createClient(getToken, { prefix: '/v1/admin', onUnauthorized });
    return { api, adminClient };
  }, []);

  // Who am I: resolved by the API from the record on every call (never from token claims),
  // fetched once per session (docs/architecture.md "Caching": session tier) and dropped on
  // sign-out. Fails soft: without it the shell still renders on the session's identity.
  const qc = useQueryClient();
  // A federated session wins: an organisation connected to its Halo signs in only through it.
  const active = federated ? sessionOf(federated) : session;
  const meQ = useQuery<Me | null>({
    queryKey: ['me', active?.user?.id ?? ''],
    enabled: Boolean(active),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async () => {
      try {
        return await clients.api.client.request<Me>('/v1/me');
      } catch {
        return null;
      }
    },
  });
  const me = meQ.data ?? null;

  const value = useMemo<AuthState>(() => {
    const { api, adminClient } = clients;
    const meta = (active?.user?.user_metadata ?? {}) as { full_name?: string; name?: string };
    // platform_role is injected into the access-token claims by the Supabase hook
    // (fn_custom_access_token), not persisted to user.app_metadata — decode it for
    // display-only gating (showing the /admin entry). The backend enforces real authz.
    // Fallback only (a deployment with the Supabase token hook, before /me answers).
    const claims = decodeJwtPayload(active?.access_token);
    const claimRole =
      (claims['platform_role'] as string | undefined) ??
      ((claims['app_metadata'] as { platform_role?: string } | undefined)?.platform_role);
    const platformRole = me?.platform_role ?? claimRole;
    return {
      session: active,
      loading: loading || fedLoading,
      configured: isAuthConfigured,
      api,
      client: api.client,
      adminClient,
      principal: {
        id: active?.user?.id,
        email: me?.user.email ?? active?.user?.email ?? null,
        name: me?.user.name ?? meta.full_name ?? meta.name ?? null,
        permissions: me?.membership?.permissions,
        platformRole,
        tenantRole: me?.membership?.role ?? null,
      },
      me,
      signOut: async () => {
        qc.removeQueries({ queryKey: ['me'] });
        if (federatedSession.get()) await federatedSession.signOut();
        if (isAuthConfigured) await getSupabase().auth.signOut();
        setSession(null);
      },
    };
  }, [active, loading, fedLoading, me, qc, clients]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
